import { Injectable } from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import jsPDF from 'jspdf';
import { firstValueFrom, forkJoin } from 'rxjs';

/* Services */

import { CallCenterService } from '../../core/services/call-center/call-center.service';
import { ClienteService } from '../services/clientes/cliente.service';
import { ConfiguracaoService } from '../services/configuracoes/configuracao.service';
import { PedidoService } from '../../core/services/pedidos/pedido.service';
import { PedidoItensService } from '../../core/services/pedidos/pedido-itens.service';
import { PrazoPagamentoItensService } from '../services/prazos-pagamento/prazo-pagamento-itens.service';
import { VisitaService } from '../services/visitas/visita.service';

/* Models */

import { CallCenterConfiguracao } from '../models/call-center/call-center-configuracao';
import { Cliente } from '../models/clientes/cliente';
import { Configuracao } from '../models/configuracoes/configuracao';
import { Pedido } from '../models/pedidos/pedido';
import { PedidoItens } from '../models/pedidos/pedido-itens';
import { PrazoPagamentoItens } from '../models/prazos-pagamento/prazo-pagamento-itens';
import { Visita } from '../models/visitas/visita';
import { VisitaImagem } from '../models/visitas/visita-imagem';

@Injectable({
  providedIn: 'root'
})

export class PdfService {
  constructor(
    private currencyPipe: CurrencyPipe,
    private datePipe: DatePipe,
    private toastr: ToastrService,
    private callCenterService: CallCenterService,
    private clienteService: ClienteService,
    private configuracaoService: ConfiguracaoService,
    private pedidoService: PedidoService,
    private pedidoItensService: PedidoItensService,
    private prazoPagamentoItensService: PrazoPagamentoItensService,
    private visitaService: VisitaService
  ) { }

  /* Pedido */

  async imprimirPedidoOrcamento(id_pedido: number, seller_id: number, porcentagem_desconto: number) {
    try {
      const documento = await this.prepararPedidoOrcamento(id_pedido, seller_id, porcentagem_desconto);
      documento.pdf.save(documento.nomeArquivo);
      this.toastr.success("", "Arquivo gerado com sucesso!", { positionClass: "toast-bottom-left" });
    } catch (error) {
      this.toastr.error("", error instanceof Error ? error.message : "Não foi possível gerar o PDF do pedido.");
    }
  }

  async gerarArquivoPedidoOrcamento(id_pedido: number, seller_id: number): Promise<File> {
    const documento = await this.prepararPedidoOrcamento(id_pedido, seller_id, 0);
    return new File([documento.pdf.output("blob")], documento.nomeArquivo, { type: "application/pdf" });
  }

  private async prepararPedidoOrcamento(id_pedido: number, seller_id: number, porcentagem_desconto: number) {
    const [callCenterConfiguracao, configuracao, pedido] = await firstValueFrom(forkJoin([
      this.callCenterService.getConfiguracaoByGuidClienteNapis(),
      this.configuracaoService.getByGuidClienteNapis(),
      this.pedidoService.getByOrder(id_pedido, seller_id)
    ]));

    if (!callCenterConfiguracao || !configuracao) {
      throw new Error("Não foi possível carregar as configurações do PDF.");
    }
    if (!pedido) {
      throw new Error("Pedido não encontrado para gerar o PDF.");
    }

    const [itens, prazoPagamentoItens] = await firstValueFrom(forkJoin([
      this.pedidoItensService.getByOrder(id_pedido, pedido.seller_id),
      this.prazoPagamentoItensService.getByIdPrazoPagamento(pedido.id_prazo_pagamento)
    ]));

    if (!itens || itens.length === 0) {
      throw new Error("O pedido não possui itens para gerar o PDF.");
    }
    if (!prazoPagamentoItens || prazoPagamentoItens.length === 0) {
      throw new Error("Não foi possível carregar as condições de pagamento do pedido.");
    }

    const pedidoItens = itens.map(item => {
      const copia = Object.assign(new PedidoItens(), item);
      if (porcentagem_desconto != null && porcentagem_desconto !== 0) {
        copia.unit_price -= copia.unit_price * porcentagem_desconto / 100;
        copia.total = copia.quantity * copia.unit_price;
      }
      return copia;
    });

    return {
      pdf: this.gerarPedidoOrcamento(callCenterConfiguracao, configuracao, pedido, pedidoItens, prazoPagamentoItens, false),
      nomeArquivo: pedido.order_id_app + ".pdf"
    };
  }

  gerarPedidoOrcamento(callCenterConfiguracao: CallCenterConfiguracao, configuracao: Configuracao, pedido: Pedido, pedidoItens: PedidoItens[], prazoPagamentoItens: PrazoPagamentoItens[], baixar: boolean = true) {
    let pdf = new jsPDF();

    if (configuracao.id == 8) {
      // UniBrazil

      pdf.addImage(callCenterConfiguracao.logotipo, 7, 2, 80, 35);
    } else if (configuracao.id == 23) {
      // Oimasa

      pdf.addImage(callCenterConfiguracao.logotipo, 9, 5, 80, 30);
    } else if (configuracao.id == 45) {
      // MOTOSUPRI Distribuidora

      pdf.addImage(callCenterConfiguracao.logotipo, 10, 5, 75, 25);
    } else if (configuracao.id == 49) {
      // Indústrias NAT LTDA

      pdf.addImage(callCenterConfiguracao.logotipo, 9, 5, 28, 28);
    } else if (configuracao.id == 66) {
      // Petrovila Química Ltda

      pdf.addImage(callCenterConfiguracao.logotipo, 10, 5, 80, 30);
    } else {
      // Brudovan / Outras

      pdf.addImage(callCenterConfiguracao.logotipo, 5, -5, 90, 40);
    }

    pdf.setFontSize(14);
    pdf.setFontType("bold");

    pdf.text(configuracao.nm_empresa == null ? "" : configuracao.nm_empresa, 200, 10, { align: "right" });

    pdf.setFontSize(12);
    pdf.setFontType("normal");

    pdf.text(configuracao.empresa_endereco == null ? "" : configuracao.empresa_endereco.trim(), 200, 15, { align: "right" });
    pdf.text(configuracao.empresa_bairro == null ? "" : configuracao.empresa_bairro.trim(), 200, 20, { align: "right" });
    pdf.text(configuracao.empresa_cidade_estado == null ? "" : configuracao.empresa_cidade_estado.trim(), 200, 25, { align: "right" });
    pdf.text(configuracao.empresa_email == null ? "" : configuracao.empresa_email.trim(), 200, 30, { align: "right" });
    pdf.text(configuracao.empresa_fone == null ? "" : "TELEFONE: " + configuracao.empresa_fone.trim(), 200, 35, { align: "right" });

    pdf.line(10, 37, 200, 37, "FD");

    pdf.setFontSize(14);
    pdf.setFontType("bold");

    if (pedido.order_sent_on != null) {
      pdf.text("PEDIDO " + pedido.order_id_app + " - " + pedido.tipo_venda, 10, 42);
    } else {
      pdf.text("ORÇAMENTO " + pedido.order_id_app, 10, 42);
    }

    /* Vendedor(a) */

    /* Cabeçalho */

    pdf.setFontSize(10);
    pdf.setFontType("bold");

    pdf.line(10, 44, 200, 44, "FD");

    pdf.text("Vendedor(a):", 10, 49);
    pdf.text("Data:", 140, 49);
    pdf.text("E-mail:", 10, 54);

    if (pedido.dt_validade != null) {
      pdf.text("Data validade:", 140, 54);
    }

    pdf.text("Telefone:", 10, 59);

    if (pedido.dt_previsao_entrega != null) {
      pdf.text("Pedido Programado:", 122, 59);
    }

    /* Dados */

    pdf.setFontType("normal");

    if (configuracao.seller_tmk_pdf) {
      pdf.text(pedido.nome_equipe == null ? "" : pedido.nome_equipe, 32, 49);
    } else {
      pdf.text(pedido.fullname == null ? "" : pedido.fullname, 32, 49);
    }

    if (pedido.order_sent_on != null) {
      pdf.text(this.datePipe.transform(pedido.order_sent_on, "dd/MM/yyyy HH:mm:ss"), 165, 49);
    } else {
      pdf.text(this.datePipe.transform(pedido.order_created_at, "dd/MM/yyyy HH:mm:ss"), 165, 49);
    }

    pdf.text(pedido.email_real == null ? "" : pedido.email_real, 32, 54);

    if (pedido.dt_validade != null) {
      pdf.text(this.datePipe.transform(pedido.dt_validade, "dd/MM/yyyy"), 165, 54);
    }

    pdf.text(pedido.celular == null ? "" : pedido.celular, 32, 59);

    if (pedido.dt_previsao_entrega != null) {
      pdf.text(this.datePipe.transform(pedido.dt_previsao_entrega, "dd/MM/yyyy"), 175, 59);
    }

    /* Cliente */

    /* Cabeçalho */

    pdf.setFontSize(10);
    pdf.setFontType("bold");

    pdf.line(10, 61, 200, 61, "FD");

    pdf.text("Cliente:", 10, 66);
    pdf.text("Endereço:", 10, 71);
    pdf.text("CNPJ/CPF:", 120, 71);
    pdf.text("Bairro:", 10, 76);
    pdf.text("IE:", 120, 76);
    pdf.text("Telefone(s):", 10, 81);
    pdf.text("Cidade:", 120, 81);
    pdf.text("E-mail:", 10, 86);
    pdf.text("CEP: ", 120, 86);

    /* Dados */

    pdf.setFontType("normal");

    pdf.text(pedido.name == null ? "" : pedido.name, 32, 66);

    if (pedido.endereco != null && pedido.end_numero != null) {
      pdf.text(pedido.endereco + ", " + pedido.end_numero, 32, 71);
    } else if (pedido.endereco != null && pedido.end_numero == null) {
      pdf.text(pedido.endereco, 32, 71);
    } else {
      pdf.text("", 32, 71);
    }

    pdf.text(pedido.client_document == null ? "" : pedido.client_document, 142, 71);
    pdf.text(pedido.bairro == null ? "" : pedido.bairro, 32, 76);
    pdf.text(pedido.ie == null ? "" : pedido.ie, 142, 76);
    pdf.text(pedido.phone == null ? "" : pedido.phone, 32, 81);
    pdf.text(pedido.cidade == null ? "" : pedido.cidade, 142, 81);
    pdf.text(pedido.email == null ? "" : pedido.email, 32, 86);
    pdf.text(pedido.cep == null ? "" : pedido.cep, 142, 86);

    /* Transportadora */

    /* Cabeçalho */

    pdf.setFontSize(10);
    pdf.setFontType("bold");

    pdf.line(10, 88, 200, 88, "FD");

    pdf.text("Transportadora:", 10, 93);

    /* Dados */

    pdf.setFontType("normal");

    pdf.text(pedido.nm_transportadora == null ? "" : pedido.nm_transportadora, 42, 93);

    /* Produtos */

    pdf.setFontType("bold");

    pdf.line(10, 95, 200, 95, "FD");

    let produtos: string = "PRODUTOS";
    let posicaoProdutos: number = ((pdf.internal.pageSize.width - ((pdf.getStringUnitWidth(produtos) * pdf.internal.getFontSize()) / pdf.internal.scaleFactor)) / 2);

    pdf.text(produtos, posicaoProdutos, 100);

    pdf.setFontSize(8);

    pdf.line(10, 102, 200, 102, "FD");

    if (configuracao.flag_exibir_imposto_produto) {
      pdf.text("#", 13, 106, { align: "center" });
      pdf.text("Código", 23, 106, { align: "center" });
      pdf.text("Descrição", 31, 106);
      pdf.text("Grupo Estoque", 100, 106, { align: "center" });
      pdf.text("Qtde", 126, 106, { align: "center" });
      pdf.text("Unitário: R$", 152, 106, { align: "right" });
      pdf.text("Imp. + F.F.: R$", 178, 106, { align: "right" });
      pdf.text("Sub-Total: R$", 200, 106, { align: "right" });
    } else {
      pdf.text("#", 13, 106, { align: "center" });
      pdf.text("Código", 25, 106, { align: "center" });
      pdf.text("Descrição", 36, 106);
      pdf.text("Grupo Estoque", 135, 106, { align: "center" });
      pdf.text("Qtde", 152, 106, { align: "center" });
      pdf.text("Unitário: R$", 178, 106, { align: "right" });
      pdf.text("Sub-Total: R$", 200, 106, { align: "right" });
    }

    pdf.setFontType("normal");

    pdf.line(10, 108, 200, 108, "FD");

    let item: number = 0;
    let itemDescricaoPartes: string[] = [];
    let linha: number = 112;
    let tamanho: number = 55;

    if (configuracao.flag_exibir_imposto_produto) {
      tamanho = 35;
    }

    let valorTotalProduto: number = 0;
    let valorTotalImposto: number = 0;
    let valorTotalFatorFinanceiro: number = 0;
    let valorTotal: number = 0;

    for (var i = 0; i < pedidoItens.length; i++) {
      item++;
      itemDescricaoPartes = this.dividirTexto(pedidoItens[i].produto_nome, tamanho);

      valorTotalProduto = (valorTotalProduto + ((pedidoItens[i].unit_price - pedidoItens[i].fator_financeiro) * pedidoItens[i].quantity));
      valorTotalImposto = (valorTotalImposto + (pedidoItens[i].valor_imposto * pedidoItens[i].quantity));
      valorTotalFatorFinanceiro = (valorTotalFatorFinanceiro + (pedidoItens[i].fator_financeiro * pedidoItens[i].quantity));

      if (configuracao.flag_exibir_imposto_produto) {
        valorTotal = (valorTotal + (pedidoItens[i].total + (pedidoItens[i].valor_imposto * pedidoItens[i].quantity)));
      } else {
        valorTotal = valorTotal + pedidoItens[i].total;
      }

      for (var j = 0; j < itemDescricaoPartes.length; j++) {
        if (j == 0) {
          if (configuracao.flag_exibir_imposto_produto) {
            pdf.text(item.toString(), 13, linha, { align: "center" });
            pdf.text(pedidoItens[i].produto_codigo == null ? "" : pedidoItens[i].produto_codigo.toString(), 23, linha, { align: "center" });
            pdf.text(itemDescricaoPartes[j], 31, linha);

            pdf.text(pedidoItens[i].grupo_estoque == null ? "" : pedidoItens[i].grupo_estoque.substring(0, 15), 100, linha, { align: "center" });
            pdf.text(pedidoItens[i].quantity.toString(), 126, linha, { align: "center" });

            /* Unitário: R$ */

            let valorUnitarioOriginal: number = pedidoItens[i].unit_price - pedidoItens[i].fator_financeiro;

            pdf.text(this.currencyPipe.transform(valorUnitarioOriginal.toString(), "", "R$"), 152, linha, { align: "right" });

            /* Imp. + F.F.: R$ */

            let valorImpostoComFatorFinanceiro: number = ((pedidoItens[i].valor_imposto + pedidoItens[i].fator_financeiro) * pedidoItens[i].quantity);

            pdf.text(this.currencyPipe.transform(valorImpostoComFatorFinanceiro, "", "R$", "1.4-4"), 178, linha, { align: "right" });

            /* Sub-Total: R$ */

            let valorImposto: number = pedidoItens[i].valor_imposto * pedidoItens[i].quantity;
            let subTotal: number = pedidoItens[i].total + valorImposto;

            pdf.text(this.currencyPipe.transform(subTotal.toString(), "", "R$"), 200, linha, { align: "right" });
          } else {
            pdf.text(item.toString(), 13, linha, { align: "center" });
            pdf.text(pedidoItens[i].produto_codigo == null ? "" : pedidoItens[i].produto_codigo.toString(), 25, linha, { align: "center" });
            pdf.text(itemDescricaoPartes[j], 36, linha);
            pdf.text(pedidoItens[i].grupo_estoque == null ? "" : pedidoItens[i].grupo_estoque.substring(0, 15), 135, linha, { align: "center" });
            pdf.text(pedidoItens[i].quantity.toString(), 152, linha, { align: "center" });
            pdf.text(this.currencyPipe.transform(pedidoItens[i].unit_price.toString(), "", "R$"), 178, linha, { align: "right" });
            pdf.text(this.currencyPipe.transform(pedidoItens[i].total.toString(), "", "R$"), 200, linha, { align: "right" });
          }
        } else {
          pdf.text(itemDescricaoPartes[j], 31, linha);
        }

        linha = linha + 5;

        if (linha > 286) {
          pdf.line(10, 286, 200, 286, "FD");

          pdf.addPage();

          linha = 10;

          pdf.line(10, linha, 200, linha, "FD");

          linha = linha + 4;
        }
      }
    }

    /* Total */

    pdf.setFontSize(10);
    pdf.setFontType("bold");

    let colunaTotalDescricao: number = 130;
    let colunaTotal: number = 200;

    /* Produto */

    let totalProdutoDescricao: string = "PRODUTOS:";
    let totalProduto = this.currencyPipe.transform(valorTotalProduto.toString(), "", "R$");

    /* Imposto */

    let totalImpostoDescricao: string = "IMPOSTOS:";
    let totalImposto = this.currencyPipe.transform(valorTotalImposto.toString(), "", "R$");

    /* Fator Financeiro */

    let totalFatorFinanceiroDescricao: string = "FATOR FINANCEIRO:";
    let totalFatorFinanceiro = this.currencyPipe.transform(valorTotalFatorFinanceiro.toString(), "", "R$");

    /* Total */

    let totalDescricao: string = "TOTAL:";
    let total = this.currencyPipe.transform(valorTotal.toString(), "", "R$");

    linha = linha - 3;

    pdf.line(10, linha, 200, linha, "FD");

    if (configuracao.flag_exibir_imposto_produto) {
      pdf.setFontType("normal");

      /* Produto */

      linha = linha + 4;

      if (linha > 286) {
        pdf.addPage();

        linha = 10;

        pdf.line(10, linha, 200, linha, "FD");

        linha = linha + 4;
      }

      pdf.text(totalProdutoDescricao, colunaTotalDescricao, linha);
      pdf.text(totalProduto, colunaTotal, linha, { align: "right" });

      /* Imposto */

      linha = linha + 4;

      if (linha > 286) {
        pdf.addPage();

        linha = 10;

        pdf.line(10, linha, 200, linha, "FD");

        linha = linha + 4;
      }

      pdf.text(totalImpostoDescricao, colunaTotalDescricao, linha);
      pdf.text(totalImposto, colunaTotal, linha, { align: "right" });

      /* Fator Financeiro */

      linha = linha + 4;

      if (linha > 286) {
        pdf.addPage();

        linha = 10;

        pdf.line(10, linha, 200, linha, "FD");

        linha = linha + 4;
      }

      pdf.text(totalFatorFinanceiroDescricao, colunaTotalDescricao, linha);
      pdf.text(totalFatorFinanceiro, colunaTotal, linha, { align: "right" });

      pdf.setFontType("bold");
    }

    /* Total */

    linha = linha + 4;

    if (linha > 286) {
      pdf.addPage();

      linha = 10;

      pdf.line(10, linha, 200, linha, "FD");

      linha = linha + 4;
    }

    pdf.text(totalDescricao, colunaTotalDescricao, linha);
    pdf.text(total, colunaTotal, linha, { align: "right" });

    /* Frete */

    if (pedido.valor_frete != null && pedido.valor_frete > 0) {
      let totalFreteDescricao: string = "FRETE:";
      let totalFrete = this.currencyPipe.transform(pedido.valor_frete.toString(), "", "R$");

      linha = linha + 4;

      if (linha > 286) {
        pdf.addPage();

        linha = 10;

        pdf.line(10, linha, 200, linha, "FD");

        linha = linha + 4;
      }

      pdf.setFontType("normal");
      pdf.text(totalFreteDescricao, colunaTotalDescricao, linha);
      pdf.text(totalFrete, colunaTotal, linha, { align: "right" });

      /* Total com Frete */

      let totalComFrete: number = valorTotal + pedido.valor_frete;
      let totalComFreteDescricao: string = "TOTAL COM FRETE:";
      let totalComFreteFormatado = this.currencyPipe.transform(totalComFrete.toString(), "", "R$");

      linha = linha + 4;

      if (linha > 286) {
        pdf.addPage();

        linha = 10;

        pdf.line(10, linha, 200, linha, "FD");

        linha = linha + 4;
      }

      pdf.setFontType("bold");
      pdf.text(totalComFreteDescricao, colunaTotalDescricao, linha);
      pdf.text(totalComFreteFormatado, colunaTotal, linha, { align: "right" });

      valorTotal = totalComFrete;
    }

    /* Simulação / Observações */

    linha = linha + 20;

    if (linha > 226) {
      pdf.addPage();

      linha = 10;

      pdf.line(10, linha, 200, linha, "FD");

      linha = linha + 4;
    }

    let linhaInicial = linha;

    pdf.line(10, linha, 200, linha, "FD");

    pdf.setFontSize(10);
    pdf.setFontType("bold");

    linha = linha + 5;

    if (linha > 286) {
      pdf.addPage();

      linha = 10;
      linhaInicial = linha;

      pdf.line(10, linha, 200, linha, "FD");

      linha = linha + 4;
    }

    pdf.text("Simulação da Condição de Pagamento:", 12, linha);
    pdf.text("Observações:", 102, linha);

    pdf.setFontType("normal");

    linha = linha + 5;

    if (linha > 286) {
      pdf.addPage();

      linha = 10;
      linhaInicial = linha;

      pdf.line(10, linha, 200, linha, "FD");

      linha = linha + 4;
    }

    pdf.text(pedido.prazo_pagamento == null ? "" : pedido.prazo_pagamento, 12, linha);

    let observacao: string = "";

    if (pedido.observacao_nf != null) {
      observacao = pdf.splitTextToSize(pedido.observacao_nf, 96);
    }

    pdf.text(observacao, 102, linha);

    linha = linha + 2;

    if (linha > 286) {
      pdf.addPage();

      linha = 10;
      linhaInicial = linha;

      pdf.line(10, linha, 200, linha, "FD");

      linha = linha + 4;
    }

    pdf.line(12, linha, 98, linha, "FD");

    linha = linha + 4;

    if (linha > 286) {
      pdf.addPage();

      linha = 10;
      linhaInicial = linha;

      pdf.line(10, linha, 200, linha, "FD");

      linha = linha + 4;
    }

    pdf.setFontSize(8);
    pdf.setFontType("bold");

    pdf.text("Parcela", 17, linha, { align: "center" });
    pdf.text("Vencimento", 52, linha, { align: "center" });
    pdf.text("Valor: R$", 98, linha, { align: "right" });

    pdf.setFontType("normal");

    linha = linha + 2;

    pdf.line(12, linha, 98, linha, "FD");

    linha = linha + 4;

    if (linha > 286) {
      pdf.addPage();

      linha = 10;
      linhaInicial = linha;

      pdf.line(10, linha, 200, linha, "FD");

      linha = linha + 4;
    }

    let subItem: number = 0;
    let valorParcela: number = (valorTotal / prazoPagamentoItens.length);
    let dataVencimento: Date = null;

    for (var i = 0; i < prazoPagamentoItens.length; i++) {
      subItem++;

      if (pedido.order_sent_on == null) {
        dataVencimento = new Date(pedido.order_created_at);
      } else {
        dataVencimento = new Date(pedido.order_sent_on);
      }

      dataVencimento.setDate(dataVencimento.getDate() + prazoPagamentoItens[i].diasvencimento);

      pdf.text(subItem.toString(), 17, linha, { align: "center" });
      pdf.text(this.datePipe.transform(dataVencimento, "dd/MM/yyyy"), 52, linha, { align: "center" });
      pdf.text(this.currencyPipe.transform(valorParcela.toString(), "", "R$"), 98, linha, { align: "right" });

      linha = linha + 5;

      if (linha > 286) {
        pdf.addPage();

        linha = 10;
        linhaInicial = linha;

        pdf.line(10, linha, 200, linha, "FD");

        linha = linha + 4;
      }
    }

    linha = linha - 3;

    pdf.line(10, linhaInicial, 10, linha, "FD");
    pdf.line(100, linhaInicial, 100, linha, "FD");
    pdf.line(200, linhaInicial, 200, linha, "FD");

    pdf.line(10, linha, 200, linha, "FD");

    linha = this.gerarResumoPontuacaoFastscan(pdf, configuracao, pedidoItens, linha + 4);

    pdf.addImage(callCenterConfiguracao.arte_rodape, 5, 265, 200, 20);

    if (baixar) {
      pdf.save(pedido.order_id_app + ".pdf");
    }
    return pdf;
  }

  /* Rotas */

  imprimirRotas(rotasAgrupadas: any) {
    /* Call Center (Configuração) */

    let callCenterConfiguracao: CallCenterConfiguracao = null;

    /* Configurações */

    let configuracao: Configuracao = null;

    /* Call Center (Configuração) */

    this.callCenterService.getConfiguracaoByGuidClienteNapis().subscribe(
      callCenterConfiguracoes => callCenterConfiguracao = callCenterConfiguracoes,
      error => { console.log("Erro: " + error) },
      () => {
        if (callCenterConfiguracao != null) {
          /* Configurações */

          this.configuracaoService.getByGuidClienteNapis().subscribe(
            configuracoes => configuracao = configuracoes,
            error => { console.log("Erro: " + error) },
            () => {
              if (configuracao != null) {
                this.gerarRotas(callCenterConfiguracao, configuracao, rotasAgrupadas);
              }
            }
          );
        }
      }
    );
  }

  gerarRotas(callCenterConfiguracao: CallCenterConfiguracao, configuracao: Configuracao, rotasAgrupadas: any) {
    let pdf = new jsPDF();

    if (callCenterConfiguracao.logotipo != null) {
      pdf.addImage(callCenterConfiguracao.logotipo, 5, -5, 90, 40);
    }

    pdf.setFontSize(14);
    pdf.setFontType("bold");

    pdf.text(configuracao.nm_empresa == null ? "" : configuracao.nm_empresa, 200, 10, { align: "right" });

    pdf.setFontSize(12);
    pdf.setFontType("normal");

    pdf.text(configuracao.empresa_endereco == null ? "" : configuracao.empresa_endereco, 200, 15, { align: "right" });
    pdf.text(configuracao.empresa_bairro == null ? "" : configuracao.empresa_bairro, 200, 20, { align: "right" });
    pdf.text(configuracao.empresa_cidade_estado == null ? "" : configuracao.empresa_cidade_estado, 200, 25, { align: "right" });
    pdf.text(configuracao.empresa_email == null ? "" : configuracao.empresa_email, 200, 30, { align: "right" });
    pdf.text(configuracao.empresa_fone == null ? "" : "TELEFONE: " + configuracao.empresa_fone, 200, 35, { align: "right" });

    pdf.line(10, 37, 200, 37, "FD");

    pdf.setFontSize(14);
    pdf.setFontType("bold");

    pdf.text("ROTAS", 10, 42);

    pdf.line(10, 44, 200, 44, "FD");

    let linha: number = 48;

    rotasAgrupadas.forEach((item) => {
      pdf.setFontSize(10);
      pdf.setFontType("bold");

      pdf.text(item.key + " (" + item.value.length + ") - Distância: " + (item.distancia_rota ?? "") + " - Objetivo: " + this.currencyPipe.transform((item.total_objetivo_de_venda ?? 0).toString(), "", "R$"), 10, linha);

      linha = linha + 2;

      pdf.line(10, linha, 200, linha, "FD");

      linha = linha + 4;

      if (linha > 286) {
        pdf.addPage();

        linha = 10;
      }

      item.value.forEach((subItem) => {
        pdf.setFontSize(6);
        pdf.setFontType("normal");

        pdf.text((subItem.name ?? "").substring(0, 50), 10, linha);

        let endereco = (subItem.endereco ?? "") + ", " + (subItem.end_numero ?? "") + " - " + (subItem.bairro ?? "");

        let cidade = "";

        if (subItem.cidade != null) {
          cidade = subItem.cidade;
        }

        if (subItem.estado != null) {
          cidade = cidade + " - " + subItem.estado;
        }

        pdf.text(endereco.substring(0, 50), 75, linha);
        pdf.text(cidade.substring(0, 40), 145, linha);
        pdf.text(subItem.distancia ?? "", 190, linha);

        linha = linha + 2;

        if (linha > 286) {
          pdf.addPage();

          linha = 10;
        }

        pdf.line(10, linha, 200, linha, "FD");

        linha = linha + 4;

        if (linha > 286) {
          pdf.addPage();

          linha = 10;
        }
      });
    });

    pdf.save("rotas.pdf");
  }

  /* Visitas (Imagens) */

  imprimirImagensVisita(visit_id_app: number) {
    /* Call Center (Configuração) */

    let callCenterConfiguracao: CallCenterConfiguracao = null;

    /* Cliente */

    let cliente: Cliente = null;

    /* Configurações */

    let configuracao: Configuracao = null;

    /* Visita */

    let visita: Visita = null;

    /* Visitas (Imagens) */

    let visitasImagens: VisitaImagem[] = [];

    /* Call Center (Configuração) */

    this.callCenterService.getConfiguracaoByGuidClienteNapis().subscribe(
      callCenterConfiguracoes => callCenterConfiguracao = callCenterConfiguracoes,
      error => { console.log("Erro: " + error) },
      () => {
        if (callCenterConfiguracao != null) {
          /* Configurações */

          this.configuracaoService.getByGuidClienteNapis().subscribe(
            configuracoes => configuracao = configuracoes,
            error => { console.log("Erro: " + error) },
            () => {
              if (configuracao != null) {
                /* Visitas */

                this.visitaService.getByVisitIdApp(visit_id_app).subscribe(
                  visitas => visita = visitas,
                  error => { console.log("Erro: " + error) },
                  () => {
                    if (visita != null) {
                      /* Clientes */

                      this.clienteService.getById(visita.client_id).subscribe(
                        clientes => cliente = clientes,
                        error => { console.log("Erro: " + error) },
                        () => {
                          /* Visitas (Imagens) */

                          this.visitaService.getVisitaImagemByVisitIdApp(visit_id_app).subscribe(
                            visitasImagem => visitasImagens = visitasImagem,
                            error => { console.log("Erro: " + error) },
                            () => {
                              visitasImagens.forEach((item) => {
                                item.image_bytes = "data:image/png;base64," + item.image_bytes;
                              });

                              this.gerarImagensVisita(callCenterConfiguracao, configuracao, visita, cliente, visitasImagens);

                              this.toastr.success("", "Arquivo gerado com sucesso!", { positionClass: "toast-bottom-left" });
                            }
                          );
                        }
                      );
                    }
                  }
                );
              }
            }
          );
        }
      }
    );
  }

  gerarImagensVisita(callCenterConfiguracao: CallCenterConfiguracao, configuracao: Configuracao, visita: Visita, cliente: Cliente, visitasImagens: VisitaImagem[]) {
    let pdf = new jsPDF();

    if (configuracao.id == 8) {
      // UniBrazil

      pdf.addImage(callCenterConfiguracao.logotipo, 7, 2, 80, 35);
    } else if (configuracao.id == 23) {
      // Oimasa

      pdf.addImage(callCenterConfiguracao.logotipo, 9, 5, 80, 30);
    } else if (configuracao.id == 45) {
      // MOTOSUPRI Distribuidora

      pdf.addImage(callCenterConfiguracao.logotipo, 10, 5, 75, 25);
    } else if (configuracao.id == 49) {
      // Indústrias NAT LTDA

      pdf.addImage(callCenterConfiguracao.logotipo, 9, 5, 28, 28);
    } else {
      // Brudovan / Outras

      pdf.addImage(callCenterConfiguracao.logotipo, 5, -5, 90, 40);
    }

    pdf.setFontSize(14);
    pdf.setFontType("bold");

    pdf.text(configuracao.nm_empresa == null ? "" : configuracao.nm_empresa, 200, 10, { align: "right" });

    pdf.setFontSize(12);
    pdf.setFontType("normal");

    pdf.text(configuracao.empresa_endereco == null ? "" : configuracao.empresa_endereco, 200, 15, { align: "right" });
    pdf.text(configuracao.empresa_bairro == null ? "" : configuracao.empresa_bairro, 200, 20, { align: "right" });
    pdf.text(configuracao.empresa_cidade_estado == null ? "" : configuracao.empresa_cidade_estado, 200, 25, { align: "right" });
    pdf.text(configuracao.empresa_email == null ? "" : configuracao.empresa_email, 200, 30, { align: "right" });
    pdf.text(configuracao.empresa_fone == null ? "" : "TELEFONE: " + configuracao.empresa_fone, 200, 35, { align: "right" });

    pdf.line(10, 37, 200, 37, "FD");

    pdf.setFontSize(14);
    pdf.setFontType("bold");

    /* Vendedor(a) */

    /* Cabeçalho */

    pdf.setFontSize(10);
    pdf.setFontType("bold");

    pdf.text("Vendedor(a):", 10, 42);
    pdf.text("E-mail:", 76, 42);
    pdf.text("Telefone:", 157, 42);

    /* Dados */

    pdf.setFontType("normal");

    pdf.text(visita.fullname, 32, 42);
    pdf.text(visita.email_real == null ? "" : visita.email_real, 89, 42);
    pdf.text(visita.celular == null ? "" : visita.celular, 174, 42);

    /* Cliente */

    /* Cabeçalho */

    pdf.setFontSize(10);
    pdf.setFontType("bold");

    pdf.line(10, 44, 200, 44, "FD");

    pdf.text("Cliente:", 10, 49);
    pdf.text("Endereço:", 10, 54);
    pdf.text("CNPJ/CPF:", 120, 54);
    pdf.text("Bairro:", 10, 59);
    pdf.text("IE:", 120, 59);
    pdf.text("Telefone(s):", 10, 64);
    pdf.text("Cidade:", 120, 64);
    pdf.text("E-mail:", 10, 69);
    pdf.text("CEP: ", 120, 69);

    /* Dados */

    pdf.setFontType("normal");

    pdf.text(cliente.name == null ? "" : cliente.name, 32, 49);

    if (cliente.endereco != null && cliente.end_numero != null) {
      pdf.text(cliente.endereco + ", " + cliente.end_numero, 32, 54);
    } else if (cliente.endereco != null && cliente.end_numero == null) {
      pdf.text(cliente.endereco, 32, 54);
    } else {
      pdf.text("", 32, 54);
    }

    pdf.text(cliente.client_document == null ? "" : cliente.client_document, 142, 54);
    pdf.text(cliente.bairro == null ? "" : cliente.bairro, 32, 59);
    pdf.text(cliente.ie == null ? "" : cliente.ie, 142, 59);
    pdf.text(cliente.phone == null ? "" : cliente.phone, 32, 64);
    pdf.text(cliente.cidade == null ? "" : cliente.cidade, 142, 64);
    pdf.text(cliente.email == null ? "" : cliente.email, 32, 69);
    pdf.text(cliente.cep == null ? "" : cliente.cep, 142, 69);

    pdf.line(10, 71, 200, 71, "FD");

    /* Visita */

    /* Cabeçalho */

    /* Horizontal */

    pdf.line(10, 76, 200, 76, "FD");
    pdf.line(10, 83, 200, 83, "FD");
    pdf.line(10, 101, 200, 101, "FD");

    pdf.setFontType("bold");

    pdf.text("Horário de Atendimento:", 12, 81);
    pdf.text("Observações:", 102, 81);

    pdf.text("Check-in:", 12, 88);
    pdf.text("Check-out:", 12, 93);
    pdf.text("Duração:", 12, 98);

    pdf.setFontType("normal");

    /* Vertical */

    pdf.line(10, 76, 10, 101, "FD");
    pdf.line(100, 76, 100, 101, "FD");
    pdf.line(200, 76, 200, 101, "FD");

    /* Dados */

    pdf.text(this.datePipe.transform(visita.check_in, "dd/MM/yyyy HH:mm:ss"), 32, 88);
    pdf.text(this.datePipe.transform(visita.check_out, "dd/MM/yyyy HH:mm:ss"), 32, 93);

    let duracao: string = "";

    if (visita.duracao != null) {
      duracao = visita.duracao.toString() + " MIN";
    }

    pdf.text(duracao, 32, 98);

    let observacao = pdf.splitTextToSize(visita.note, 96);

    pdf.text(observacao, 102, 88);

    pdf.setFontType("bold");

    pdf.text("Fotos:", 10, 107);

    /* Visitas (Imagens) */

    let coluna: number = 10;
    let linha: number = 112;
    let idCategoria: number = -1;
    let novaLinha: boolean = false;

    visitasImagens.forEach((item) => {
      if (idCategoria != item.id_categoria && item.id_categoria != null) {
        if (idCategoria != -1) {
          coluna = 10;

          if (!novaLinha) {
            linha = linha + 55;
          }
        }

        if (linha > 278) {
          pdf.addPage();

          linha = 10;
        }

        if (item.categoria != null) {
          pdf.text(item.categoria, 10, linha);
        } else {
          pdf.text("", 10, linha);
        }

        linha = linha + 3;

        if (linha > 278) {
          pdf.addPage();

          linha = 10;
        }
      }

      if (novaLinha) {
        novaLinha = !novaLinha;
      }

      pdf.addImage(item.image_bytes, coluna, linha, 30, 50);

      coluna = coluna + 35;

      if ((coluna + 30) > 200) {
        coluna = 10;
        linha = linha + 55;

        if (linha > 278) {
          pdf.addPage();

          linha = 10;
        }

        novaLinha = true;
      }

      idCategoria = item.id_categoria;
    });

    pdf.save(visita.visit_id_app + ".pdf");
  }

  /* Auxiliares */

  private gerarResumoPontuacaoFastscan(pdf: jsPDF, configuracao: Configuracao, pedidoItens: PedidoItens[], linha: number): number {
    const resumoPontuacao = this.obterResumoPontuacaoFastscan(configuracao, pedidoItens);

    if (resumoPontuacao.length === 0) {
      return linha;
    }

    const alturaTabela = 15 + (resumoPontuacao.length * 7) + 7;

    if (linha + alturaTabela > 255) {
      pdf.addPage();
      linha = 10;
    }

    const x = 10;
    const largura = 96;
    const colunaProduto = 46;
    const colunaPontos = 34;
    const colunaQuantidade = 16;
    const totalPontuacao = resumoPontuacao.reduce((total, item) => total + item.quantidade, 0);

    pdf.setFontSize(10);
    pdf.setFontType("bold");
    pdf.text("PONTUACAO CASTROL FASTSCAN", x + (largura / 2), linha, { align: "center" });

    linha += 4;

    pdf.setFillColor(0, 118, 143);
    pdf.rect(x, linha, largura, 7, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(8);
    pdf.text("PRODUTO", x + 2, linha + 5);
    pdf.text("PONTOS POR LITRO", x + colunaProduto + 2, linha + 5);
    pdf.text("QUANTIDADE", x + colunaProduto + colunaPontos + colunaQuantidade, linha + 5, { align: "right" });

    pdf.setTextColor(0, 0, 0);
    linha += 7;

    resumoPontuacao.forEach((item, index) => {
      if (index % 2 === 0) {
        pdf.setFillColor(207, 233, 244);
        pdf.rect(x, linha, largura, 7, "F");
      }

      pdf.setDrawColor(180, 210, 220);
      pdf.line(x, linha, x + largura, linha);
      pdf.line(x + colunaProduto, linha, x + colunaProduto, linha + 7);
      pdf.line(x + colunaProduto + colunaPontos, linha, x + colunaProduto + colunaPontos, linha + 7);

      pdf.setFontType("normal");
      pdf.text(item.produto.substring(0, 25), x + 2, linha + 5);
      pdf.text(this.formatarNumeroPontuacao(item.pontosPorLitro), x + colunaProduto + colunaPontos - 2, linha + 5, { align: "right" });
      pdf.text(this.formatarNumeroPontuacao(item.quantidade), x + largura - 2, linha + 5, { align: "right" });

      linha += 7;
    });

    pdf.line(x, linha, x + largura, linha);
    pdf.setFontType("bold");
    pdf.text("TOTAL = " + this.formatarNumeroPontuacao(totalPontuacao), x + largura - 2, linha + 5, { align: "right" });

    pdf.setDrawColor(0, 0, 0);
    pdf.setTextColor(0, 0, 0);

    return linha + 8;
  }

  private obterResumoPontuacaoFastscan(configuracao: Configuracao, pedidoItens: PedidoItens[]): Array<{ produto: string, pontosPorLitro: number, quantidade: number }> {
    if (!this.deveExibirResumoPontuacaoFastscan(configuracao)) {
      return [];
    }

    const agrupado: { [subgrupo: string]: { produto: string, pontosPorLitro: number, quantidade: number } } = {};

    pedidoItens.forEach((item) => {
      const subgrupo = item.subgrupo_estoque2 == null ? "" : item.subgrupo_estoque2.toString().trim();
      const quantidade = Number(item.pontuacao || 0);

      if (subgrupo === "" || quantidade <= 0) {
        return;
      }

      const pontosPorLitro = Number(item.produto_pontuacao || 0);

      if (agrupado[subgrupo] == null) {
        agrupado[subgrupo] = {
          produto: subgrupo,
          pontosPorLitro: pontosPorLitro,
          quantidade: 0
        };
      }

      agrupado[subgrupo].pontosPorLitro = Math.max(agrupado[subgrupo].pontosPorLitro, pontosPorLitro);
      agrupado[subgrupo].quantidade += quantidade;
    });

    return Object.keys(agrupado)
      .map((key) => agrupado[key])
      .sort((a, b) => a.produto.localeCompare(b.produto));
  }

  private deveExibirResumoPontuacaoFastscan(configuracao: Configuracao): boolean {
    const guid = configuracao != null && configuracao.guidclientenapis != null
      ? configuracao.guidclientenapis.toString().toUpperCase()
      : "";

    return guid === "1737A2B1-EF48-42C3-A5E1-9C95BD74FA1D"
      || guid === "82CD91F3-FD1E-46D5-9F2F-F759E7335CBA";
  }

  private formatarNumeroPontuacao(valor: number): string {
    const numero = Number(valor || 0);

    if (Math.round(numero) === numero) {
      return numero.toString();
    }

    return numero.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  dividirTexto(texto: string, tamanho: number): string[] {
    let partes: string[] = [];

    for (var i = 0; i < texto.length; i += tamanho) {
      partes.push(texto.substring(i, i + tamanho));
    }

    return partes;
  }
}
